import { useId, useState } from 'react';

import { HONEYPOT_FIELD, type Role } from '../../shared/contract';
import { cx } from '../lib/cx';
import { RoleToggle } from './RoleToggle';
import styles from './WaitlistForm.module.css';

interface WaitlistFormProps {
  /** True while a submission is in flight. */
  busy: boolean;
  /** User-facing error from the last attempt, if any. */
  errorMessage?: string | undefined;
  onSubmit: (input: { email: string; role: Role; honeypot: string }) => void;
  /** Called on edit so a stale error clears as soon as the visitor retypes. */
  onEdit: () => void;
}

const PRIVACY_NOTE = 'No spam. One confirmation email, then one more when we launch.';

/**
 * Email capture with the creator/buyer toggle.
 *
 * Purely presentational: it owns the field values but knows nothing about where
 * a signup goes. That lives in `useWaitlistSignup`, which keeps this component
 * trivial to render in isolation.
 */
export function WaitlistForm({ busy, errorMessage, onSubmit, onEdit }: WaitlistFormProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('creator');
  const [honeypot, setHoneypot] = useState('');
  const emailId = useId();

  function handleSubmit(event: React.SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmit({ email, role, honeypot });
  }

  return (
    <>
      <RoleToggle value={role} onChange={setRole} disabled={busy} />

      {/* `noValidate`: we render our own message rather than a browser bubble,
          so validation feedback matches the rest of the page. */}
      <form onSubmit={handleSubmit} noValidate>
        <div className={styles.emailRow}>
          <input
            id={emailId}
            className={styles.input}
            type="email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              onEdit();
            }}
            placeholder="you@email.com"
            autoComplete="email"
            aria-label="Email address"
            aria-invalid={errorMessage !== undefined}
            required
          />
          <button type="submit" className={styles.submit} disabled={busy}>
            {busy ? 'Joining…' : 'Join waitlist'}
          </button>
        </div>

        <input
          className={styles.honeypot}
          type="text"
          name={HONEYPOT_FIELD}
          value={honeypot}
          onChange={(event) => {
            setHoneypot(event.target.value);
          }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
      </form>

      <div className={cx(styles.message, errorMessage && styles.error)} role="status" aria-live="polite">
        {errorMessage}
      </div>

      <div className={styles.privacyNote}>{PRIVACY_NOTE}</div>
    </>
  );
}
